/**
 * Dependency-locator fetchers fossabot can open an upgrade PR for. fossabot can
 * open PRs for more package managers than this, but Create PR is only offered
 * where it also supports impact and change detection. Adding an ecosystem is a
 * one-line change here, plus its case in fetcherToFossabotPackageManager.
 */
export const FOSSABOT_SUPPORTED_FETCHERS = new Set<string>(['npm', 'mvn', 'pip']);

/**
 * Status of a fossabot dependency-upgrade PR for a FOSSA issue.
 *
 * 'none'         no PR has been created for this issue
 * 'creating'     PR is being created
 * 'analyzing'    PR created, fossabot is analyzing (pair with
 *                isAnalysisDelayed for the "analysis delayed" treatment)
 * 'analysisNotRun' PR created without fossabot analysis
 * 'needsReview'  fossabot analysis completed and requires human review
 * 'readyToMerge' review complete, waiting to merge
 * 'merged'       PR has been merged
 * 'closed'       PR was closed on the VCS without merging
 * 'failed'       PR creation job failed, or hit a user error
 */
export type FossabotPRStatus =
  | 'none'
  | 'creating'
  | 'analyzing'
  | 'analysisNotRun'
  | 'needsReview'
  | 'readyToMerge'
  | 'merged'
  | 'closed'
  | 'failed';

export type FossabotPRDetails = {
  /** Current status of the PR. */
  status: FossabotPRStatus;
  /** PR URL in the VCS (GitHub, GitLab, etc.). Null until the PR exists. */
  vcsUrl: string | null;
  /** PR URL in the fossabot UI. Null until the PR exists. */
  fossabotUrl: string | null;
  /**
   * True while status is 'analyzing' and the analysis is taking longer than
   * expected (or failed and should be retried).
   */
  isAnalysisDelayed: boolean;
  /**
   * Creation-job id, present while a creation job is known. Pass it back on
   * the status route: a failed job is only visible through its id.
   */
  jobId: string | null;
  /**
   * Customer-facing reason a creation job failed, chosen from fossabot's
   * failureCode. Null unless status is 'failed' and the code is one we know.
   */
  failureMessage: string | null;
};

export type FossabotCreditLevel = 'available' | 'low' | 'exhausted';

/** Setup state reported by fossabot for the requested scope. */
export type FossabotReadinessStatus = 'READY' | 'SYNCING' | 'LINK_REQUIRED' | 'FAILED';

export type FossabotConnectionStatus = {
  /** The org (or, with projectLocator, that project's repo) is set up in fossabot. */
  connected: boolean;
  /** Whether Core obtained a fossabot status for the requested scope. */
  statusAvailable: boolean;
  /** Base URL of the fossabot app. Null when the integration is not configured. */
  appUrl: string | null;
  /** Credit standing reported by fossabot. Null when credits are unavailable. */
  creditLevel: FossabotCreditLevel | null;
  /** Setup state for the requested scope; null when Core has no readiness value. */
  readinessStatus: FossabotReadinessStatus | null;
};

export type FossabotPRVcsState = 'open' | 'draft' | 'closed' | 'merged';

export type FossabotPRSort = 'newest' | 'oldest';

/** Analysis badge on a PRs-tab row; null when no analysis ran or it failed. */
export type FossabotPRAnalysisBadge = 'scanning' | 'analyzed' | 'needsReview';

export type FossabotPRListItem = {
  title: string;
  /** PR number on the VCS provider. Null when fossabot has no numeric id. */
  prNumber: number | null;
  vcsUrl: string | null;
  /** PR detail page in the fossabot app. Null when fossabot did not give one. */
  fossabotUrl: string | null;
  state: FossabotPRVcsState;
  analysisBadge: FossabotPRAnalysisBadge | null;
  /** FOSSA user id of the initiator; null for scheduled/automated PRs. */
  initiatedByUserId: number | null;
  /**
   * Display name of the initiator, resolved from initiatedByUserId. Null for
   * automated PRs and for ids with no user in the calling organization.
   */
  initiatedByName: string | null;
  openedAt: string;
  closedAt: string | null;
  mergedAt: string | null;
};

/** Counts for the PRs-tab stat cards and filter chips. */
export type FossabotPRCounts = {
  total: number;
  open: number;
  draft: number;
  analyzed: number;
  needsReview: number;
  closed: number;
  merged: number;
};
